import { useState } from 'react';
import {
  useAuthSession,
  useBillingGateResult,
  usePromotionTargets,
  useTranslation,
} from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';
import { PromotionKitPage } from '@/features/promotion-kit/components/promotion-kit-page';
import { MarketingTabs } from '@/features/promotional-ads/components/marketing-tabs';
import { usePromotionalAdsAccess } from '@/features/promotional-ads/hooks/use-promotional-ads-access';

const PAGE_SIZE = 25;

export function PromotionKitRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  // Epic #228 P7: the one Marketing gate of this route; the tab bar takes its state.
  const adsAccess = usePromotionalAdsAccess();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const targetsQuery = usePromotionTargets({
    client: supabase,
    storeId,
    supabaseUrl: env.VITE_SUPABASE_URL,
    search,
    limit: PAGE_SIZE,
    offset,
  });
  const paidToolsGateQuery = useBillingGateResult({
    client: supabase,
    storeId,
    gate: 'promotion_kit_paid_tools',
  });
  const analyticsGateQuery = useBillingGateResult({
    client: supabase,
    storeId,
    gate: 'promotion_analytics',
  });

  const page = targetsQuery.data;
  const hasMore = page?.groups.some((group) => group.hasMore) ?? false;
  const isLoading =
    targetsQuery.isLoading || paidToolsGateQuery.isLoading || analyticsGateQuery.isLoading;
  const hasError =
    targetsQuery.isError ||
    paidToolsGateQuery.isError ||
    analyticsGateQuery.isError ||
    !page ||
    !paidToolsGateQuery.data ||
    !analyticsGateQuery.data;

  return (
    <main
      dir={dir}
      data-testid="promotion-kit-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mb-6">
        <MarketingTabs active="kit" accessState={adsAccess.state} />
        <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight">
          {t('admin.promotionKit.title' as TranslationKey)}
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-start text-sm">
          {t('admin.promotionKit.subtitle' as TranslationKey)}
        </p>
      </div>

      {isLoading ? (
        <div
          className="border-border bg-card shadow-admin-card rounded-lg border p-8"
          aria-busy="true"
        >
          <div className="bg-muted h-5 w-48 animate-pulse rounded" />
          <div className="bg-muted mt-4 h-40 animate-pulse rounded" />
        </div>
      ) : null}

      {!isLoading && hasError ? (
        <div
          role="alert"
          data-testid="promotion-kit-route-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" aria-hidden />
          <h2 className="text-foreground mb-2 text-xl font-semibold">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t(
              (targetsQuery.isError
                ? 'admin.promotionKit.errors.permissionDenied'
                : 'admin.entitlements.promotionKitBlocked') as TranslationKey,
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void targetsQuery.refetch();
              void paidToolsGateQuery.refetch();
              void analyticsGateQuery.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {!isLoading && page && !page.hasAnyAvailableTarget ? (
        <div className="border-border bg-card shadow-admin-card rounded-lg border p-8 text-center">
          {t('admin.promotionKit.empty.noPublicTargets' as TranslationKey)}
        </div>
      ) : null}

      {!isLoading && page?.hasAnyAvailableTarget ? (
        <PromotionKitPage
          targetsPage={page}
          paidToolsGate={paidToolsGateQuery.data ?? null}
          analyticsGate={analyticsGateQuery.data ?? null}
          publicUrlConfig={{ publicStorefrontOrigin: env.VITE_PUBLIC_STOREFRONT_URL }}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setOffset(0);
          }}
          onLoadMore={() => setOffset((current) => current + PAGE_SIZE)}
          hasMore={hasMore}
        />
      ) : null}
    </main>
  );
}
